import { FC, useEffect } from 'react';
import { Button, Group, InlineNotification, Link, Text, lightV2Theme } from '@logz-ui/styleguide';
import { ThemeProvider } from 'styled-components';
import { NEW_APP_ROOT, resolveNewAppUrl } from '../../../../hooks/new-application-redirect/resolve-new-app-url';
import {
  OPEN360_MOVE_NOTICE_CAMPAIGN,
  OPEN360_MOVE_NOTICE_COPY,
  OPEN360_MOVE_NOTICE_SNOOZE_DAYS,
  OPEN360_MOVE_NOTICE_WHAT_CHANGES_URL,
} from './constants';
import { analyticsService } from 'services/analytics/analytics.service';
import { appRouter } from 'services/router/router.service';
import { hideModalInNewApp } from 'ui/utils/hide-modal-in-new-app';

const ANALYTICS_FEATURE = 'Open 360 application migration';

// userId, accountId and isImpersonating are added by analyticsService; these say where and which wave.
const getEventProps = (): Record<string, unknown> => ({
  campaign: OPEN360_MOVE_NOTICE_CAMPAIGN,
  page: appRouter.globals.current?.name,
});

const handleTry = (): void => {
  const url = resolveNewAppUrl(window.location);

  analyticsService.capture(ANALYTICS_FEATURE, 'Clicked on Try Open 360 AI from move notice banner', {
    ...getEventProps(),
    isDeepLink: url !== NEW_APP_ROOT,
  });
  hideModalInNewApp();
  // eslint-disable-next-line security/detect-non-literal-fs-filename -- window.open, not fs.open
  window.open(url, '_blank');
};

const handleWhatChanges = (): void => {
  analyticsService.capture(ANALYTICS_FEATURE, 'Clicked on What changes from move notice banner', getEventProps());
};

// The title slot is custom JSX, so it doesn't get the variant's text color the way a string title would.
const { textColor } = lightV2Theme.inlineNotification.variants.warning.bold;

export interface IOpen360MoveNoticeProps {
  onSnooze: () => void;
}

// Rendered only while useOpen360MoveNotice says so; the layout owns that hook to keep the sneak-peek banners out.
export const Open360MoveNotice: FC<IOpen360MoveNoticeProps> = ({ onSnooze }) => {
  const handleClose = (): void => {
    analyticsService.capture(ANALYTICS_FEATURE, 'Dismissed move notice banner', {
      ...getEventProps(),
      snoozeDays: OPEN360_MOVE_NOTICE_SNOOZE_DAYS,
    });
    onSnooze();
  };

  useEffect(() => {
    analyticsService.capture(ANALYTICS_FEATURE, 'Viewed move notice banner', getEventProps());
  }, []);

  // Always light: the styleguide's dark warning-bold puts light text on the same yellow. styled-components'
  // provider, not LogzioThemeProvider, which would also flip <html> (and the whole app) to light.
  return (
    <ThemeProvider theme={lightV2Theme}>
      <InlineNotification
        variant="warning"
        bold
        sharpCorners
        // Two 14px lines (~36px) plus 7px top and bottom keeps the bar at 50px.
        py="7px"
        shouldCenterCloseIcon
        subject="open360-move-notice"
        onClose={handleClose}
        title={
          <Group alignItems="center" justifyContent="space-between" fullWidth>
            <Group vertical gap={0}>
              <Text weight={700} color={textColor}>
                {OPEN360_MOVE_NOTICE_COPY.title}
              </Text>
              <Text color={textColor}>{OPEN360_MOVE_NOTICE_COPY.message}</Text>
            </Group>
            <Group alignItems="center">
              <Button size="s" variant="contrast" onClick={handleTry}>
                {OPEN360_MOVE_NOTICE_COPY.tryLabel}
              </Button>
              <Link href={OPEN360_MOVE_NOTICE_WHAT_CHANGES_URL} underline onClick={handleWhatChanges}>
                {OPEN360_MOVE_NOTICE_COPY.whatChangesLabel}
              </Link>
            </Group>
          </Group>
        }
      />
    </ThemeProvider>
  );
};
